import * as vp from 'vopay';
import type { InterestAccountTransactionHistoryStatus } from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from './InterestAccountsPage.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

const { withStyles, Select, TextField, Button, DateRangePicker, AVAILABLE_TIMEZONES } = vp;
const classes = withStyles(styles);

type TransactionSearchParams = {
  type: string;
  accountType: string;
  status: InterestAccountTransactionHistoryStatus;
  timeZone: vp.Timezone;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
};

type SearchFormProps = {
  type: string;
  status: InterestAccountTransactionHistoryStatus;
  searchTerm: string;
  hasInvalidEndDate: boolean;
  hasLongTermAccount: boolean;
  shouldShowEndDateError: boolean;
  setShouldShowEndDateError: (event: boolean) => void;
  onSearch: (searchParams: TransactionSearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const InterestAccountTransactionSearchForm: FC = (props: SearchFormProps) => {
  const {
    type,
    status,
    searchTerm,
    hasInvalidEndDate,
    hasLongTermAccount,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    onSearch,
  } = props;

  const { userSettings } = useUserSettingsProvider();
  const { response } = useClientData();
  const [currentType, setCurrentType] = useState<string>(type);
  const [currentStatus, setCurrentStatus] = useState<InterestAccountTransactionHistoryStatus>(status);
  const [currentTimeZone, setCurrentTimeZone] = useState<vp.Timezone>(userSettings.timezone);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [currentAccountType, setCurrentAccountType] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>();
  const typeOptions: { [key: string]: string } = {
    All: 'All',
    'EFT Withdrawal': 'EFT Withdrawal',
    'EFT Funding': 'EFT Funding',
    'VoPayInstant Withdraw': 'VoPayInstant Withdraw',
    Refund: 'Refund',
    'Currency Conversion': 'Currency Conversion',
    'Interac Money Request': 'Interac Money Request',
    'Interac Bulk Payout': 'Interac Bulk Payout',
    'Inbound e-Transfer': 'Inbound e-Transfer',
    'Visa Direct Push': 'Visa Direct Push',
    'Credit Card': 'Credit Card',
    'Google Pay': 'Google Pay',
    'PayPal Withdrawal': 'PayPal Withdrawal',
    'Account Transfer': 'Account Transfer',
    Reversal: 'Reversal',
    'Bill Pay': 'Bill Pay',
    'Auto Balance Transfer': 'Auto Balance Transfer',
    'Virtual Account Deposit': 'Virtual Account Deposit',
    'Virtual Account Funding': 'Virtual Account Funding',
    'Virtual Account Withdraw': 'Virtual Account Withdraw',
    'Virtual Account Wire': 'Virtual Account Wire',
    'Other Funding': 'Other Funding',
    'Other Withdrawal': 'Other Withdrawal',
  };
  const typeOptionsUS: { [key: string]: string } = {
    All: 'All',
    'ACH Withdrawal': 'ACH Withdrawal',
    'ACH Funding': 'ACH Funding',
    'RTP Withdrawal': 'RTP Withdrawal',
    'Fedwire Withdrawal': 'Fedwire Withdrawal',
    'Fedwire Funding': 'Fedwire Funding',
    Refund: 'Refund',
    'Currency Conversion': 'Currency Conversion',
    'Visa Direct Push': 'Visa Direct Push',
    'Credit Card': 'Credit Card',
    'Google Pay': 'Google Pay',
    'PayPal Withdrawal': 'PayPal Withdrawal',
    'Venmo Withdrawal': 'Venmo Withdrawal',
    'Account Transfer': 'Account Transfer',
    Reversal: 'Reversal',
    'Auto Balance Transfer': 'Auto Balance Transfer',
    'Virtual Account Deposit': 'Virtual Account Deposit',
    'Virtual Account Funding': 'Virtual Account Funding',
    'Virtual Account Withdraw': 'Virtual Account Withdraw',
    'Virtual Account Wire': 'Virtual Account Wire',
    'Other Funding': 'Other Funding',
    'Other Withdrawal': 'Other Withdrawal',
  };

  const statusOptions: Record<Exclude<InterestAccountTransactionHistoryStatus, null>, string> = {
    All: 'All',
    waiting: 'Waiting',
    pending: 'Pending',
    successful: 'Successful',
  };

  const accountTypeOptions = {
    All: 'All',
    'short-term': 'Short Term',
    'long-term': 'Long Term',
  };

  const typeOptionsUSArray = Object.keys(typeOptionsUS).map((key) => ({
    value: key,
    label: typeOptionsUS[key],
  }));

  const typeOptionsArray = Object.keys(typeOptions).map((key) => ({
    value: key,
    label: typeOptions[key],
  }));

  const accountTypeOptionsArray = Object.entries(accountTypeOptions).map(([key]) => ({
    value: key,
    label: accountTypeOptions[key as keyof typeof accountTypeOptions],
  }));

  const statusOptionsArray = Object.entries(statusOptions).map(([key]) => ({
    value: key,
    label: statusOptions[key as keyof typeof statusOptions],
  }));

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  return (
    <div className={classes('interestAccountTransactionSearchFormContainer')}>
      <DateRangePicker
        triggerVariant="outlineShadow"
        onChange={(range) => {
          if (range && range[0] && range[1]) {
            setTimeRange(range);
          }
        }}
        initialTime={timeRange}
        // placeholder={['From', 'To']}
        hasInvalidEndDate={hasInvalidEndDate}
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
      />

      {/* <Box maxwidth="160px" width="160px" ov="hidden" className={classes('dropdown')}> */}
      <Select
        version="2"
        placeholder="Trans. Type"
        options={response.Country === 'US' ? typeOptionsUSArray : typeOptionsArray}
        name="type"
        value={currentType}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentType(e.target.value);
        }}
      />
      {/* </Box> */}

      {hasLongTermAccount && (
        <Select
          version="2"
          placeholder="Acc. Type"
          options={accountTypeOptionsArray}
          name="status"
          value={currentAccountType}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentAccountType(e.target.value);
          }}
        />
      )}
      <Select
        version="2"
        placeholder="Status"
        options={statusOptionsArray}
        name="status"
        value={currentStatus || ''}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentStatus(e.target.value as InterestAccountTransactionHistoryStatus);
        }}
      />
      <Select
        version="2"
        placeholder="Timezone"
        options={timeZoneOptions}
        name="timeZone"
        value={currentTimeZone}
        onBlur={() => {}}
        filterable
        onChange={(e) => {
          setCurrentTimeZone(e.target.value as vp.Timezone);
        }}
        sort={false}
      />
      <TextField
        key="TransactionSearchKeyword"
        name="TransactionSearchKeyword"
        placeholder="Keyword"
        value={currentSearchTerm}
        onBlur={() => {}}
        onChange={(e) => {
          setCurrentSearchTerm(e.target.value);
        }}
        variant="outlineShadow"
      />

      <Button
        className={styles.searchButton}
        id="search-transaction-history"
        label="Search"
        width="100px"
        borderRadius="10px"
        height="51px"
        isDisabled={!currentType && !currentAccountType && !currentStatus && !currentSearchTerm && !timeRange}
        onClick={() => {
          timeRange &&
            onSearch({
              type: currentType,
              accountType: currentAccountType,
              status: currentStatus,
              timeZone: currentTimeZone,
              searchTerm: currentSearchTerm,
              timeRange,
            });
        }}
      />
    </div>
  );
};

export default InterestAccountTransactionSearchForm;
